import { ImdbMovieData } from '@/types/model';
import { imdbApiService, ImdbEpisodeData, ImdbTVShowData } from '@/services/imdbApiService';
import { apiClient } from '@/services/apiClient';

export type ContentDetailsSource = 'IMDB' | 'TMDB';
export type ContentDetailsKind = 'movie' | 'tvShow' | 'episode';
export type TmdbSearchKind = 'movie' | 'tvShow';

export interface TmdbMatchCriteria extends Record<string, string | number | boolean | undefined> {
  imdbId?: string;
  title?: string;
  year?: number;
}

export interface TmdbSearchResult {
  tmdbId: string;
  title: string;
  originalTitle?: string;
  year?: number;
  overview?: string;
  posterImageUrl?: string;
  originalLanguage?: string;
  rating?: number;
  popularity?: number;
  adult?: boolean;
}

export interface ContentDetailsProviderParams {
  source: ContentDetailsSource;
  imdbId?: string;
  tvShowImdbId?: string;
  tmdbId?: string;
  tvShowTmdbId?: string;
  seasonNumber?: number;
  episodeNumber?: number;
}

export interface ContentDetails {
  source: ContentDetailsSource;
  kind: ContentDetailsKind;
  imdbId?: string;
  tmdbId?: string;
  title: string;
  spanishTitle?: string;
  originalTitle?: string;
  year?: number;
  releaseDate?: string;
  firstAirDate?: string;
  lastAirDate?: string;
  airDate?: string;
  durationMinutes?: number;
  rating?: number;
  actors?: string;
  creators?: string;
  englishSynopsis?: string;
  spanishSynopsis?: string;
  coverImageUrl?: string;
  backgroundImageUrl?: string;
  classification?: string;
  genres?: string[];
  totalSeasons?: number;
  totalEpisodes?: number;
  seasonNumber?: number;
  episodeNumber?: number;
}

interface ContentDetailsProvider {
  getMovie(params: ContentDetailsProviderParams): Promise<ContentDetails>;
  getTVShow(params: ContentDetailsProviderParams): Promise<ContentDetails>;
  getEpisode(params: ContentDetailsProviderParams): Promise<ContentDetails>;
}

const contentDetailsProviders: Record<ContentDetailsSource, ContentDetailsProvider> = {
  IMDB: {
    async getMovie(params) {
      const imdbId = requireImdbId(params.imdbId);
      const movieData: ImdbMovieData = await imdbApiService.getMovieByImdbId(imdbId);

      if (!movieData) {
        throw new Error('Película no encontrada en IMDB');
      }

      if (!['tvMovie', 'movie'].includes(movieData.type)) {
        throw new Error('El ID de IMDB proporcionado no corresponde a una película.');
      }

      const [releaseDate, titles, classification] = await Promise.all([
        imdbApiService.getReleaseDateByImdbId(imdbId).catch((error) => {
          console.warn('Failed to fetch release date from IMDB:', error);
          return null;
        }),
        imdbApiService.getTitles(imdbId).catch((error) => {
          console.warn('Failed to fetch titles from IMDB:', error);
          return [];
        }),
        imdbApiService.getClassificationByImdbId(imdbId, 'movie').catch((error) => {
          console.warn('Failed to fetch classification from IMDB:', error);
          return null;
        }),
      ]);

      const { extractSpanishAndOriginalTitle } = await import('@/utils/imdbTitles');
      const extractedTitles = extractSpanishAndOriginalTitle(titles, {
        originCountryCode: movieData.originCountries?.[0]?.code,
        primaryTitle: movieData.primaryTitle,
      });

      return {
        source: 'IMDB',
        kind: 'movie',
        imdbId,
        title: movieData.primaryTitle,
        spanishTitle: extractedTitles.spanishTitle,
        originalTitle: extractedTitles.originalTitle,
        year: movieData.startYear,
        releaseDate: releaseDate || undefined,
        durationMinutes: movieData.runtimeSeconds ? Math.round(movieData.runtimeSeconds / 60) : undefined,
        rating: movieData.rating?.aggregateRating ?? 0,
        actors: (movieData.stars || []).map((star) => star.displayName).join(', '),
        englishSynopsis: movieData.plot,
        coverImageUrl: movieData.primaryImage?.url,
        classification: classification || undefined,
        genres: movieData.genres,
      };
    },

    async getTVShow(params) {
      const imdbId = requireImdbId(params.imdbId);
      const tvShowData: ImdbTVShowData = await imdbApiService.getTVShowByImdbId(imdbId);

      if (!tvShowData) {
        throw new Error('Serie no encontrada en IMDB');
      }

      if (!['series', 'tvMiniSeries', 'tvSeries'].includes(tvShowData.type)) {
        throw new Error('El ID de IMDB proporcionado no corresponde a una serie de TV.');
      }

      const [firstAirDate, titles, classification] = await Promise.all([
        imdbApiService.getReleaseDateByImdbId(imdbId).catch((error) => {
          console.warn('Failed to fetch TV show release date from IMDB:', error);
          return null;
        }),
        imdbApiService.getTitles(imdbId).catch((error) => {
          console.warn('Failed to fetch titles from IMDB:', error);
          return [];
        }),
        imdbApiService.getClassificationByImdbId(imdbId, 'series').catch((error) => {
          console.warn('Failed to fetch TV show classification from IMDB:', error);
          return null;
        }),
      ]);

      const { extractSpanishAndOriginalTitle } = await import('@/utils/imdbTitles');
      const extractedTitles = extractSpanishAndOriginalTitle(titles, {
        originCountryCode: tvShowData.originCountries?.[0]?.code,
        primaryTitle: tvShowData.primaryTitle,
      });

      return {
        source: 'IMDB',
        kind: 'tvShow',
        imdbId,
        title: tvShowData.primaryTitle,
        spanishTitle: extractedTitles.spanishTitle,
        originalTitle: extractedTitles.originalTitle,
        year: tvShowData.startYear,
        firstAirDate: firstAirDate || undefined,
        rating: tvShowData.rating?.aggregateRating ?? 0,
        actors: (tvShowData.stars || []).map((star) => star.displayName).join(', '),
        creators: (tvShowData.creators || []).map((creator) => creator.displayName).join(', '),
        englishSynopsis: tvShowData.plot,
        coverImageUrl: tvShowData.primaryImage?.url,
        classification: classification || undefined,
        genres: tvShowData.genres,
        totalSeasons: tvShowData.totalSeasons || 1,
        totalEpisodes: tvShowData.totalEpisodes || 1,
      };
    },

    async getEpisode(params) {
      const imdbId = requireImdbId(params.imdbId);
      const episodeData: ImdbEpisodeData = await imdbApiService.getEpisodeByImdbId(imdbId);

      if (!episodeData) {
        throw new Error('Episodio no encontrado en IMDB');
      }

      if (episodeData.type !== 'tvEpisode') {
        throw new Error('El ID de IMDB proporcionado no corresponde a un episodio.');
      }

      let position = {
        seasonNumber: episodeData.seasonNumber,
        episodeNumber: episodeData.episodeNumber,
      };

      const seriesImdbId = params.tvShowImdbId;
      if ((!position.seasonNumber || !position.episodeNumber) && seriesImdbId) {
        position = {
          ...position,
          ...(await imdbApiService.findEpisodePositionInSeries(seriesImdbId, imdbId).catch((error) => {
            console.warn('Failed to resolve episode season/number via IMDB episodes list:', error);
            return null;
          })),
        };
      }

      const releaseDate = await imdbApiService.getReleaseDateByImdbId(imdbId).catch((error) => {
        console.warn('Failed to fetch episode release date from IMDB:', error);
        return null;
      });

      return {
        source: 'IMDB',
        kind: 'episode',
        imdbId,
        title: episodeData.primaryTitle,
        durationMinutes: episodeData.runtimeSeconds ? Math.round(episodeData.runtimeSeconds / 60) : undefined,
        rating: episodeData.rating?.aggregateRating ?? 0,
        englishSynopsis: episodeData.plot,
        coverImageUrl: episodeData.primaryImage?.url,
        airDate: releaseDate || undefined,
        seasonNumber: position.seasonNumber,
        episodeNumber: position.episodeNumber,
      };
    },
  },
  TMDB: {
    async getMovie(params) {
      const tmdbId = requireTmdbId(params.tmdbId);
      return apiClient.get<ContentDetails>(`/content-details/tmdb/movies/${encodeURIComponent(tmdbId)}`);
    },

    async getTVShow(params) {
      const tmdbId = requireTmdbId(params.tmdbId);
      return apiClient.get<ContentDetails>(`/content-details/tmdb/tv-shows/${encodeURIComponent(tmdbId)}`);
    },

    async getEpisode(params) {
      const tvShowTmdbId = requireTmdbId(params.tvShowTmdbId, 'ID de TMDB de la serie');
      const seasonNumber = requirePositiveNumber(params.seasonNumber, 'temporada');
      const episodeNumber = requirePositiveNumber(params.episodeNumber, 'episodio');
      return apiClient.get<ContentDetails>(
        `/content-details/tmdb/tv-shows/${encodeURIComponent(tvShowTmdbId)}/seasons/${seasonNumber}/episodes/${episodeNumber}`
      );
    },
  },
};

export const contentDetailsService = {
  getMovie(params: ContentDetailsProviderParams) {
    return contentDetailsProviders[params.source].getMovie(params);
  },
  getTVShow(params: ContentDetailsProviderParams) {
    return contentDetailsProviders[params.source].getTVShow(params);
  },
  getEpisode(params: ContentDetailsProviderParams) {
    return contentDetailsProviders[params.source].getEpisode(params);
  },
  searchTmdb(kind: TmdbSearchKind, query: string) {
    const resource = kind === 'movie' ? 'movies' : 'tv-shows';
    return apiClient.get<TmdbSearchResult[]>(`/content-details/tmdb/search/${resource}`, { query });
  },
  matchTmdb(kind: TmdbSearchKind, criteria: TmdbMatchCriteria) {
    const resource = kind === 'movie' ? 'movies' : 'tv-shows';
    return apiClient.get<TmdbSearchResult[]>(`/content-details/tmdb/match/${resource}`, criteria);
  },
};

function requireImdbId(value?: string): string {
  const id = value?.trim();
  if (!id) {
    throw new Error('Ingrese un ID de IMDB.');
  }
  return id;
}

function requireTmdbId(value?: string, label = 'ID de TMDB'): string {
  const id = value?.trim();
  if (!id) {
    throw new Error(`Ingrese el ${label}.`);
  }
  return id;
}

function requirePositiveNumber(value: number | undefined, label: string): number {
  if (!value || Number.isNaN(value) || value < 1) {
    throw new Error(`Ingrese un número de ${label} válido.`);
  }
  return value;
}
